/* eslint-disable @typescript-eslint/camelcase,no-param-reassign */
import React, {
  useEffect, useRef, useCallback, useContext, useMemo,
} from 'react';
import './editor.scss';
import ReactDOM from 'react-dom';
import Message from '@/component/Message';
import { observer, useLocalStore } from 'mobx-react';
import { storeContext } from '@/pages/document/models/store';
import { useTranslation } from 'react-i18next';
import { SylApi, EventChannel } from '@syl-editor/core-utilities';
import {
  Config,
  BlockQuotePlugin,
  BoldPlugin,
  BulletListPlugin,
  CodeBlockPlugin,
  FontFamilyPlugin,
  FontSizePlugin,
  HrPlugin,
  ItalicPlugin,
  ListItemPlugin,
  SubPlugin,
  SupPlugin,
  OrderedListPlugin,
  StrikePlugin,
  UnderlinePlugin,
  Header1Plugin,
  Header2Plugin,
  Header3Plugin,
  FormatClearPlugin,
  AlignLeftUtil,
  AlignCenterUtil,
  AlignRightUtil,
  ToolbarInlineLoader,
  // 以下为0.4.0新增
  LineHeightUtil,
  ParagraphPlugin,
} from '@syl-editor/lib';
import {
  SylEditor,
  ImagePlugin,
  LinkPlugin,
  BackgroundPlugin,
  ColorPlugin,
  VideoPlugin,
} from '@syl-editor/react-syl-editor-v2';
import { ToolbarInline } from '@syl-editor/react-syl-editor-v2/dist/lib/modules/toolbar/render/inline';
import { ToolbarOutlineLoader } from './Plugin/ToolbarOutline';
import { ToolbarOutline } from './Plugin/render';
import { TablePlugin } from '@syl-editor/lib/dist/lib/formats/table';
import { fileUpload } from '@/pages/document/utils';
import Placeholder from './Placeholder';
import DIYLink from './Plugin/Link/link';
import DIYBackground from './Plugin/Background';
import DIYColor from './Plugin/Color';
import { Tooltips, Toolicons } from '@/pages/document/const';

interface Props{
  mounted?: (editor: SylApi) => void;
  change?: (context: string) => void;
}

const RichEditor: React.FC<Props> = ({ mounted = () => {}, change = () => {} }) => {
  const { t } = useTranslation();
  const toolBar = useRef();
  const input = useRef();
  const { documentStore } = useContext(storeContext);

  const getAdapter = useCallback((editor: SylApi) => {
    State.editor = editor as any;
    editor.setHTML(documentStore.activeDocDraftContent);
    mounted(editor);
    editor.on(EventChannel.LocalEvent.TEXT_CHANGED, () => {
      documentStore.docHasEdit = editor.getHTML() === documentStore.activeDocEditContent;
      documentStore.activeDocEditContent = editor.getHTML() || '';
      change(editor.getHTML() as string);
    });
  }, []);

  const State = useLocalStore(() => ({
    editor: null,
    isRender: false,
    id: 0,
  }));

  const config: Config = useMemo(() => ({
    Plugins: [
      {
        plugin: ParagraphPlugin,
        controllerProps: {},
      },
      {
        plugin: ImagePlugin,
        controllerProps: {
          // 图片上传方法，接受一个'blob'或'file'，返回图片地址或者对象
          // 如果是从外部粘贴的图片，uploader入参会是一个string
          // Promise<string | { src: string, width: number, height: number }>,
          uploader: async (img: Blob | File | string) => {
            State.id++;
            try {
              (State.editor as any).insertShadow({
                shadow: (dom: any) => {
                  ReactDOM.render(
                    <Placeholder />,
                    dom,
                  );
                  return dom;
                },
                spec: {
                  key: `image-${State.id}`,
                },
              });
              const result = await fileUpload(img);
              (State.editor as any).removeShadow(`image-${State.id}`);
              return new Promise((resolve) => {
                resolve(result);
              });
            } catch (err) {
              // eslint-disable-next-line no-console
              console.log(err);
            }
          },
          onUploadError: () => {
            Message.error(t('upload_img_error'));
          },
          // uploadType: 'blob', // 可选，uploader接受的文件类型， 默认'blob'
          uploadBeforeInsert: true, // 可选，上传完成后插入，默认false
        },
      },
      {
        plugin: VideoPlugin,
        controllerProps: {
          uploader: async (video: Blob | File | string, editor: SylApi) => {
            State.id++;
            editor.insertShadow({
              shadow: (dom) => {
                ReactDOM.render(
                  <Placeholder id={State.id} />,
                  dom,
                );
                return dom;
              },
              spec: {
                key: `video-${State.id}`,
              },
            });
            const result: any = await fileUpload(video, 'VIDEO', {
              onUploadProgress: (progress: any) => {
                // eslint-disable-next-line no-mixed-operators
                const p = `${Math.round(progress.loaded / progress.total * 100)}%`;
                const conatiner = document.getElementById(`myProgress-${State.id}`);
                const num = document.getElementById(`myProgressNum-${State.id}`);
                if (conatiner && num) {
                  conatiner.style.width = p;
                  num.innerText = p;
                }
              },
            });
            const { index } = editor.removeShadow(`video-${State.id}`) as any;
            if (result.src) {
              editor.insertCard('video', { ...result }, index);
            }
          },
        },
      },
      {
        plugin: CodeBlockPlugin,
        controllerProps: {
          importStyle: false,
        },
      },
      {
        plugin: TablePlugin,
        controllerProps: {
          // 菜单按钮相关配置
          button: {
            trigger: 'click', // 菜单触发方式
          },
        },
      },
      SupPlugin, DIYLink, DIYBackground, DIYColor,
      BackgroundPlugin, ColorPlugin, BlockQuotePlugin,
      BoldPlugin, BulletListPlugin, FontFamilyPlugin,
      FontSizePlugin, FormatClearPlugin, Header1Plugin,
      Header2Plugin, Header3Plugin, HrPlugin, SubPlugin,
      ItalicPlugin, OrderedListPlugin, ListItemPlugin,
      StrikePlugin, UnderlinePlugin, LinkPlugin,
    ],
    Modules: {
      toolbarOutline: {
        Ctor: ToolbarOutlineLoader,
        option: {
          Component: ToolbarOutline,
          tools: [
            'header1', 'header2', 'header3',
            'block_quote', 'bullet_list', 'ordered_list',
            'video', 'image', 'hr', 'table', 'code_block',
          ],
          tooltips: Tooltips,
          icons: Object.keys(Toolicons).reduce((origin: any, next) => {
            origin[next] = <span className={(Toolicons as any)[next]} />;
            return origin;
          }, {}),
        },
      },
      toolbar: {
        Ctor: ToolbarInlineLoader,
        option: {
          Component: ToolbarInline,
          tools: [
            'bold', 'italic', 'underline', 'strike',
            'diyLink', 'diyBackground', 'diyColor',
            'align_left', 'align_center', 'align_right',
            ['header1', 'header2', 'header3', 'bullet_list',
              'ordered_list', 'code_block', 'block_quote'],
          ],
          tooltips: Tooltips,
          icons: Object.keys(Toolicons).reduce((origin: any, next) => {
            origin[next] = <span className={(Toolicons as any)[next]} />;
            return origin;
          }, {}),
          utils: {
            align_left: AlignLeftUtil,
            align_right: AlignRightUtil,
            align_center: AlignCenterUtil,
            line_height: LineHeightUtil,
          },
        },
      },
    },
  }), []);

  useEffect(() => {
    State.isRender = true;
    // 获取焦点
    (input.current as any).focus();
  }, []);

  const titleKeyUp = useCallback((e) => {
    if ((e.keyCode || e.which) === 13) {
      (State.editor as any).focus();
    }
  }, []);

  const changeActiveDocTitle = useCallback((e) => {
    const title = e.target.value;
    documentStore.activeDocEditTitle = title;
    documentStore.docHasEdit = true;
  }, []);

  return (
    <div className="rich-editor-container">
      <div ref={toolBar as any} className="toolbar" />
      <div className="context-box">
        <div className="rich-editor-title">
          <input
            ref={input as any}
            value={documentStore.activeDocEditTitle}
            placeholder={t('doc_title_placeholder')}
            onKeyUp={titleKeyUp}
            onChange={changeActiveDocTitle}
          />
        </div>
        {State.isRender && (
          <SylEditor
            placeholder={t('please_input_context')}
            getAdapter={getAdapter}
            config={config}
          />
        )}
      </div>
    </div>
  );
};

export default React.memo(observer(RichEditor));
